/** @jsxImportSource @emotion/react */

import React, { FC } from 'react'
import { Form as FinalForm } from 'react-final-form'
import Button from '@mui/material/Button'
import arrayMutators from 'final-form-arrays'
import Typography from '@mui/material/Typography'
import { FormApi, SubmissionErrors } from 'final-form'
import { useNavigate, useParams } from 'react-router-dom'
import createDecorator from 'final-form-focus'
import { InputField } from 'components/Input'
import { validateRequired } from 'utils/validation'
import { PriceInput } from 'components/Input/PriceInput'
import { DropzoneField, ImageField } from 'components/DropzoneField'
import { SelectField } from 'components/Select'
import { CheckboxField } from 'components/Checkbox'
import { ProductStatus } from '__generatedTypes__/globalTypes'
import { setFieldData } from 'utils/setFieldData'
import { PlatformSlug } from 'constants/consts'
import * as classes from './styles'

export type Status = { id: ProductStatus; title: string }

export const STATUSES: Array<Status> = [
  {
    id: ProductStatus.Active,
    title: 'Active',
  },
  {
    id: ProductStatus.Inactive,
    title: 'Inactive',
  },
]

const DARK_MODE_ONLY_PLATFORMS = [PlatformSlug.CARSBooster]

const DROPZONE_OPTIONS = {
  accept: {
    'image/*': [],
  },
}

export type FormValues = {
  name?: string
  description?: string
  count?: number
  price?: number
  status?: Status
  isPopular?: boolean
  imageLight?: ImageField[]
  imageDark?: ImageField[]
}

type Props = {
  initialValues: FormValues
  onSubmit: (
    values: FormValues,
    form: FormApi<FormValues>,
    callback?: (errors?: SubmissionErrors) => void
  ) => SubmissionErrors | Promise<SubmissionErrors> | void
}

const focusOnErrors = createDecorator<FormValues, Partial<FormValues>>()

export const Form: FC<Props> = ({ initialValues, onSubmit }) => {
  const { platform = '' } = useParams()

  const navigate = useNavigate()

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        Edit Pause Token Product
      </Typography>
      <FinalForm<FormValues>
        mutators={{ ...arrayMutators, setFieldData }}
        decorators={[focusOnErrors]}
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ handleSubmit, submitting }) => (
          <form css={classes.form} onSubmit={handleSubmit}>
            <InputField name="name" validate={validateRequired} label="Name" placeholder="Enter Name" fullWidth />
            <PriceInput
              name="count"
              validate={validateRequired}
              label="Number of tokens"
              placeholder="Enter Number of tokens"
              decimalScale={0}
              fullWidth
            />
            <PriceInput
              name="price"
              validate={validateRequired}
              label="Price"
              placeholder="Enter Price"
              prefix="$"
              fullWidth
            />
            <InputField
              name="description"
              validate={validateRequired}
              label="Description"
              placeholder="Enter Description"
              fullWidth
              multiline
            />
            <SelectField
              name="status"
              validate={validateRequired}
              items={STATUSES}
              label="Status"
              placeholder="Choose Status"
              displaySelectedItemsFirst
              fullWidth
            />
            <CheckboxField name="isPopular" label="Is this product most popular?" />
            <div css={classes.columnsWrapper}>
              {!DARK_MODE_ONLY_PLATFORMS.includes(platform as PlatformSlug) ? (
                <div>
                  <div css={classes.labelWrapper}>Light Version</div>
                  <DropzoneField
                    name="imageLight"
                    validate={validateRequired}
                    dropzoneOptions={DROPZONE_OPTIONS}
                    options={{
                      maxFiles: 1,
                      sliceFromEnd: true,
                    }}
                  />
                </div>
              ) : null}
              <div>
                <div css={classes.labelWrapper}>Dark Version</div>
                <DropzoneField
                  name="imageDark"
                  validate={validateRequired}
                  dropzoneOptions={DROPZONE_OPTIONS}
                  options={{
                    maxFiles: 1,
                    sliceFromEnd: true,
                  }}
                />
              </div>
            </div>

            <div css={classes.buttonsWrapper}>
              <Button
                variant="outlined"
                color="red"
                size="large"
                fullWidth
                disabled={submitting}
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button variant="contained" type="submit" color="blue" size="large" fullWidth disabled={submitting}>
                Update Product
              </Button>
            </div>
          </form>
        )}
      />
    </section>
  )
}
